<template>
  <div
    :class="['vch-scroller', isDrag ? 'vch-scroller--drag' : null]"
    ref="wrapper"
  >
    <button
      type="button"
      v-show="canAdvanceBackward"
      class="slider-navigation-button slider-navigation-prev"
      @click.prevent="navAction('prev')"
      :aria-disabled="!canAdvanceBackward"
      :aria-describedby="ariaTitle"
      :aria-label="prvAriaLabel"
    >
      <svg width="40" height="40" role="img" :aria-label="prvAriaLabel">
        <use href="/assets/sprite/icons.svg#btn-next" />
      </svg>
    </button>
    <div
      ref="scrollable"
      class="vch-scroller__scrollable"
      @scroll="scrollPosition = $event.target.scrollLeft"
      @mousedown="drag"
    >
      <slot />
    </div>
    <button
      type="button"
      v-show="canAdvanceForward"
      class="slider-navigation-button slider-navigation-next"
      @click.prevent="navAction('next')"
      :aria-disabled="!canAdvanceForward"
      :aria-label="nxtAriaLabel"
      :aria-describedby="ariaTitle"
    >
      <svg width="40" height="40" role="img">
        <use href="/assets/sprite/icons.svg#btn-next" />
      </svg>
    </button>
  </div>
</template>

<script>
import { globalResizeObserver } from 'Utils/';
import { setupCardImpressions } from 'Mixins/setupCardImpressions/setupCardImpressions';

const REF_SCROLLABLE = 'scrollable';

export default {
  name: 'VchScroller',
  mixins: [setupCardImpressions],
  props: {
    format: {
      type: String,
      default: 'medium',
    },
    cardType: {
      type: String,
    },
    radioScroller: {
      type: Boolean,
      default: false,
    },
    ariaTitle: {
      type: String,
      default: '',
    },
    productItemId: {
      type: String,
      default: '',
    },
    varintItemId: {
      type: String,
      default: '',
    },
    prvAriaLabel:{
      type: String,
      required: false,
      default: 'Previous'
    },
    nxtAriaLabel:{
      type: String,
      required: false,
      default: 'Next'
    }
  },
  data() {
    return {
      scrollPosition: 0,
      isDrag: false,
      offset: 0,
      microMoveMargin: 15,
      observer: null,
      label: '',
      impressions: [],
    };
  },
  computed: {
    maxWidth() {
      return this.scrollPosition >= this.offset - 1;
    },
    canAdvanceForward() {
      return !this.maxWidth;
    },
    canAdvanceBackward() {
      return this.scrollPosition > this.microMoveMargin;
    },
  },
  mounted() {
    globalResizeObserver.observe(
      this.$refs[REF_SCROLLABLE],
      this.setSizes,
      200
    );
    document.addEventListener('keyup', this.onKeyUp);
    if (this.cardType === 'product') {
      this.setupCardImpressions();
    }
    this.varientComponentAPI();
  },
  updated() {
    if (this.canAdvanceForward) {
      this.setActiveClass();
      this.setTabIndex();
    }
  },
  beforeDestroy() {
    globalResizeObserver.unobserve(this.$refs[REF_SCROLLABLE]);
    document.removeEventListener('keyup', this.onKeyUp);
    this.observer.disconnect();
  },
  methods: {
    onKeyUp(event) {
      const elementIsFocused = this.$refs['wrapper'].matches(':focus-within');
      if (this.radioScroller || !elementIsFocused) return;
      if (event.keyCode === 37 && this.scrollPosition) {
        this.navAction('prev');
      } else if (event.keyCode === 39) {
        this.navAction('next');
      }
    },
    navAction(direction) {
      let stepWidth;
      if (this.radioScroller) {
        stepWidth = this.$refs['wrapper'].offsetWidth;
      } else {
        stepWidth = 278;
      }
      if (direction === 'next') {
        this.scrollPosition =
          this.scrollPosition === 0
            ? stepWidth
            : this.scrollPosition + stepWidth;
      } else {
        this.scrollPosition = this.scrollPosition - stepWidth;
      }

      if (this.radioScroller) {
        let found = false;
        this.$refs[REF_SCROLLABLE].querySelectorAll('li').forEach((listEl) => {
          if (found) return;
          if (listEl.offsetLeft >= this.scrollPosition) {
            const newSelection = listEl.querySelector('input');
            if (newSelection) newSelection.checked = true;
            found = true;
          }
        });
      }

      this.moveFocus(direction);

      this.$refs[REF_SCROLLABLE].scrollTo({
        left: this.scrollPosition,
        behavior: 'smooth',
      });
    },
    setSizes() {
      const { scrollWidth, clientWidth } = this.$refs[REF_SCROLLABLE];
      this.offset = scrollWidth - clientWidth;
    },
    drag(e) {
      const pos = {
        left: this.$refs[REF_SCROLLABLE].scrollLeft,
        x: e.clientX,
      };
      const move = (e) => {
        e.preventDefault();
        let dx = e.clientX - pos.x;
        this.isDrag = this.isDrag || Math.abs(dx) > this.microMoveMargin;
        this.$refs[REF_SCROLLABLE].scrollLeft = pos.left - dx;
      };
      const release = () => {
        this.isDrag = false;
        document.removeEventListener('mousemove', move);
        document.removeEventListener('mouseup', release);
      };
      document.addEventListener('mousemove', move);
      document.addEventListener('mouseup', release);
    },
    setActiveClass() {
      let callback = (entries) => {
        entries.forEach((entry) => {
          !entry.isIntersecting
            ? entry.target.classList.add('--hide')
            : entry.target.classList.remove('--hide');
        });
      };
      this.observer = new IntersectionObserver(callback, {
        threshold: [0.1],
      });
      const items = document.getElementsByClassName('vch-slider__item');
      for (let item of items) {
        this.observer.observe(item);
      }
    },
    setTabIndex() {
      let callback = (entries) => {
        entries.forEach((entry) => {
          if (entry.target.querySelector('a.button')) {
            !entry.isIntersecting
              ? entry.target
                  .querySelector('a.button')
                  .setAttribute('tabindex', -1)
              : entry.target
                  .querySelector('a.button')
                  .setAttribute('tabindex', 0);
          }
        });
      };
      this.observer = new IntersectionObserver(callback, {
        threshold: [1],
      });

      const items = document.getElementsByClassName('vch-slider__item');
      for (let item of items) {
        this.observer.observe(item);
      }
    },
    //move the focus to the newly displayed item
    moveFocus(dir) {
      if (dir == 'next') {
        const cardItems = this.$refs[REF_SCROLLABLE].querySelectorAll('li');
        const itemsList = Array.from(cardItems);
        const lastFocusElement = itemsList
          .filter((item) => item.querySelector('[tabindex="0"]'))
          .slice(-1)[0];
        if (lastFocusElement) {
          setTimeout(() => {
            lastFocusElement.nextElementSibling
              .querySelector('.button')
              .focus();
          }, 100);
        }
      }
      if (dir == 'prev') {
        setTimeout(() => {
          const cardItems = this.$refs[REF_SCROLLABLE].querySelectorAll('li');
          const itemsList = Array.from(cardItems);
          const firstFocusElement = itemsList.filter((item) =>
            item.querySelector('[tabindex="0"]')
          )[0];
          if (firstFocusElement) {
            firstFocusElement.querySelector('.button').focus();
          }
        }, 500);
      }
    },
    varientComponentAPI() {
      if (this.productItemId !== '' && this.varintItemId !== '') {
        setTimeout(() => {
          window.eventBus.$emit('rfp-mctb-has-varient-component', {
            productItemId: this.productItemId.toUpperCase(),
            varintItemId: this.varintItemId.toUpperCase(),
          });
        }, 100);
      }
    },
  },
};
</script>
<style lang="scss" src="./scroller.scss"></style>
